/**
 * Matters API Hook
 */

import { useApiData, useAuthenticatedFetch } from './client';
import type { Matter } from './types';

export function useMatters(options?: {
  underClientId?: number;
  limit?: number;
  orderBy?: string;
  ascending?: boolean;
  /** Cache-buster: change to force a refetch (backend ignores it). */
  refreshKey?: number;
}) {
  // NOTE: api-edna accepts `limit`, `order_by`, and `ascending` only. It has no
  // `skip`/`offset`/cursor, and no eager-loading flags — collections are capped at
  // 1000 with truncation signalled in the `X-Result-Truncated` response header.
  // Options it ignores were removed rather than left as silent no-ops.
  const params = new URLSearchParams();
  if (options?.underClientId !== undefined) params.set('under_client_id', String(options.underClientId));
  if (options?.limit !== undefined) params.set('limit', String(options.limit));
  if (options?.orderBy) params.set('order_by', options.orderBy);
  if (options?.ascending !== undefined) params.set('ascending', String(options.ascending));
  if (options?.refreshKey) params.set('_r', String(options.refreshKey));

  const queryString = params.toString();
  const endpoint = queryString ? `/matters?${queryString}` : '/matters';

  return useApiData<Matter[]>(endpoint);
}

export function useMatter(matterId: number) {
  return useApiData<Matter>(`/matters/${matterId}`);
}

export function useMatterMutations() {
  const { fetchData } = useAuthenticatedFetch<Matter>();

  const createMatter = (data: { name: string; id_client: number; mattercode?: string }) =>
    fetchData('/matters', {
      method: 'POST',
      body: JSON.stringify({ mattercode: '', ...data, creator: '', ts: new Date().toISOString() }),
    });

  const updateMatter = (id: number, data: { name?: string; mattercode?: string }) =>
    fetchData(`/matters/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

  const deleteMatter = (id: number) =>
    fetchData(`/matters/${id}`, { method: 'DELETE' });

  return { createMatter, updateMatter, deleteMatter };
}
